import { Component, OnInit, OnDestroy } from '@angular/core';
import { Platform } from '@ionic/angular';

@Component({
  selector: 'app-pwa-install',
  template: `
    <div *ngIf="showInstallBanner" class="install-banner">
      <div class="install-content">
        <ion-icon name="rocket"></ion-icon>
        <div class="install-text">
          <strong>Instalar SAT Vendedores</strong>
          <p>Instala la app en tu dispositivo</p>
        </div>
        <ion-button fill="solid" (click)="installPWA()">
          Instalar
        </ion-button>
        <ion-button fill="clear" (click)="dismiss()">
          <ion-icon name="close"></ion-icon>
        </ion-button>
      </div>
    </div>
  `,
  styles: [`
    .install-banner {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: linear-gradient(135deg, var(--ion-color-primary) 0%, var(--ion-color-secondary) 100%);
      color: white;
      padding: 16px;
      box-shadow: 0 -4px 12px rgba(0,0,0,0.15);
      z-index: 9999;
      animation: slideUp 0.3s ease-out;
    }

    .install-content {
      display: flex;
      align-items: center;
      gap: 12px;
      max-width: 600px;
      margin: 0 auto;
    }

    .install-text {
      flex: 1;
    }

    .install-text strong {
      display: block;
      font-size: 16px;
      margin-bottom: 4px;
    }

    .install-text p {
      margin: 0;
      font-size: 14px;
      opacity: 0.9;
    }

    ion-icon {
      font-size: 24px;
      color: white;
    }

    ion-button {
      --color: white;
      --background: rgba(255,255,255,0.2);
      --background-hover: rgba(255,255,255,0.3);
      --border-radius: 8px;
      font-weight: 600;
    }

    ion-button[fill="clear"] {
      --background: transparent;
      --color: white;
    }

    @keyframes slideUp {
      from {
        transform: translateY(100%);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    @media (max-width: 480px) {
      .install-content {
        flex-wrap: wrap;
        text-align: center;
      }
      
      .install-text {
        width: 100%;
        text-align: center;
      }
      
      ion-button {
        flex: 1;
        min-width: 100px;
      }
    }
  `]
})
export class PwaInstallComponent implements OnInit, OnDestroy {
  showInstallBanner = false;
  deferredPrompt: any;
  isMobile = false;
  isStandalone = false;

  constructor(private platform: Platform) {}

  ngOnInit() {
    this.detectDevice();
    this.checkIfShouldShow();
    this.setupInstallPrompt();
  }

  ngOnDestroy() {
    // Cleanup listeners if needed
  }

  detectDevice() {
    this.isMobile = this.platform.is('mobile') || this.platform.is('mobileweb');
    this.isStandalone = this.platform.is('mobile') && (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    );
  }

  checkIfShouldShow() {
    // Solo mostrar si:
    // 1. Es móvil
    // 2. No está ya instalado (standalone)
    // 3. No fue descartado antes
    const wasDismissed = localStorage.getItem('pwa-install-dismissed');
    this.showInstallBanner = this.isMobile && !this.isStandalone && !wasDismissed;
  }

  setupInstallPrompt() {
    if (!this.showInstallBanner) return;

    // Escuchar el evento beforeinstallprompt
    window.addEventListener('beforeinstallprompt', (e: any) => {
      e.preventDefault();
      this.deferredPrompt = e;
    });
  }

  async installPWA() {
    if (!this.deferredPrompt) {
      // Si no hay prompt, intentar instalación manual
      this.showManualInstructions();
      return;
    }

    try {
      // Mostrar el prompt de instalación
      this.deferredPrompt.prompt();
      
      // Esperar respuesta del usuario
      const { outcome } = await this.deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        this.dismiss();
        
        // Cerrar el navegador y abrir la app instalada
        this.openInstalledApp();
      } else {
      }
      
      this.deferredPrompt = null;
    } catch (error) {
      console.error('Error en instalación PWA:', error);
      this.showManualInstructions();
    }
  }

  openInstalledApp() {
    // Esperar un momento para que la instalación se complete
    setTimeout(() => {
      // Mostrar mensaje al usuario
      this.showInstallationCompleteMessage();
      
      // Intentar abrir la app instalada con lógica mejorada
      this.tryOpenInstalledApp();
    }, 2000); // Esperar 2 segundos
  }

  tryOpenInstalledApp() {
    if (this.platform.is('android')) {
      // Android: Intentar cerrar navegador y abrir app
      this.openAndroidPWA();
    } else {
      // iOS y otros: Redirigir a la misma URL (abrirá como PWA si está instalada)
      this.redirectToApp();
    }
  }

  openAndroidPWA() {
    try {
      const url = window.location.origin + window.location.pathname;
      
      // Intentar abrir la app PWA instalada
      window.open(url, '_blank');
      
      // Esperar un momento y luego intentar cerrar la pestaña actual
      setTimeout(() => {
        try {
          window.close();
          
          // Si no se puede cerrar (normal), redirigir con mensaje
          setTimeout(() => {
            this.showAndroidInstructions();
          }, 500);
        } catch (error) {
          // Si no se puede cerrar, mostrar instrucciones
          this.showAndroidInstructions();
        }
      }, 1000);
    } catch (error) {
      this.showAndroidInstructions();
    }
  }

  redirectToApp() {
    // Para iOS y otros: simplemente redirigir a la misma URL
    // Si la app está instalada, se abrirá como PWA
    // Si no, se quedará en el navegador
    setTimeout(() => {
      window.location.href = window.location.href;
    }, 1000);
  }

  showAndroidInstructions() {
    // Mostrar instrucciones específicas para Android
    const message = document.createElement('div');
    message.innerHTML = `
      <div style="
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: var(--ion-color-primary);
        color: white;
        padding: 20px;
        border-radius: 12px;
        z-index: 10000;
        max-width: 300px;
        text-align: center;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3);
        animation: slideIn 0.3s ease-out;
      ">
        <div style="font-size: 24px; margin-bottom: 12px;">📱</div>
        <strong>¡App instalada!</strong>
        <p style="margin: 12px 0; font-size: 14px;">
          Cierra esta pestaña y abre la app desde tu pantalla de inicio
        </p>
        <button onclick="this.parentElement.parentElement.remove()" style="
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
        ">
          Entendido
        </button>
      </div>
      <style>
        @keyframes slideIn {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
      </style>
    `;
    document.body.appendChild(message);
    
    // Auto-eliminar después de 8 segundos
    setTimeout(() => {
      if (message.parentNode) {
        message.parentNode.removeChild(message);
      }
    }, 8000);
  }

  showInstallationCompleteMessage() {
    // Crear un toast o alert para informar al usuario
    const message = document.createElement('div');
    message.innerHTML = `
      <div style="
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: var(--ion-color-success);
        color: white;
        padding: 16px 24px;
        border-radius: 8px;
        z-index: 10000;
        font-weight: 500;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        animation: fadeInOut 3s ease-in-out;
      ">
        ✅ ¡App instalada correctamente!
      </div>
      <style>
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
          20% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          80% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
        }
      </style>
    `;
    document.body.appendChild(message);
    
    // Eliminar el mensaje después de la animación
    setTimeout(() => {
      if (message.parentNode) {
        message.parentNode.removeChild(message);
      }
    }, 3000);
  }

  showManualInstructions() {
    // Mostrar instrucciones manuales según el dispositivo
    let message = '';
    
    if (this.platform.is('ios')) {
      message = 'Para instalar: Toca el botón "Compartir" y luego "Agregar a pantalla de inicio"';
    } else if (this.platform.is('android')) {
      message = 'Para instalar: Toca el menú (⋮) y luego "Instalar aplicación" o "Agregar a pantalla de inicio"';
    } else {
      message = 'Busca la opción "Instalar aplicación" o "Agregar a pantalla de inicio" en el menú del navegador';
    }

    // Mostrar alert con instrucciones
    alert(message);
  }

  dismiss() {
    this.showInstallBanner = false;
    localStorage.setItem('pwa-install-dismissed', 'true');
  }
}
